import { formatCurrency } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

/**
 * One line of a money waterfall.
 *
 * - `start`: the amount the waterfall begins with (what customers paid).
 * - `minus`: a deduction, shown with a leading minus. The amount is the
 *   positive figure the API sends (a fee, a cost) — it is never negated here.
 * - `signed`: an amount shown with its own sign (a refund adjustment ≤ 0).
 * - `result`: a subtotal or the final figure, emphasised with a rule above it.
 * - `note`: context that is not part of the flow (discounts already taken off
 *   what customers paid), shown muted and without a sign.
 */
export interface WaterfallStep {
  label: string;
  amount: number;
  kind: 'start' | 'minus' | 'signed' | 'result' | 'note';
  /** Extra emphasis for the final line. */
  final?: boolean;
}

/**
 * Collected → deductions → profit, as a vertical statement. Every figure comes
 * straight from the API: the frontend lays the steps out and does no
 * arithmetic (CLAUDE.md, "Money"). Used by the financial report and by
 * settlement statements.
 */
export function MoneyWaterfall({
  steps,
  currency,
  label,
  className,
}: {
  steps: readonly WaterfallStep[];
  currency: string;
  /** Accessible name of the list, e.g. "ILS waterfall". */
  label: string;
  className?: string;
}) {
  return (
    <dl aria-label={label} className={cn('text-xs', className)}>
      {steps.map((step) => {
        const muted = step.kind === 'minus' || step.kind === 'note';
        return (
          <div
            key={step.label}
            className={cn(
              'flex items-baseline justify-between gap-3 py-1',
              step.kind === 'result' && 'border-border mt-0.5 border-t pt-1.5 font-semibold',
              step.kind === 'note' && 'italic',
              step.final && 'text-sm',
            )}
          >
            <dt className={cn(muted && 'text-muted-foreground', step.kind === 'minus' && 'ps-3')}>
              {step.label}
            </dt>
            <dd className={cn('tabular-nums', muted && 'text-muted-foreground')}>
              {step.kind === 'minus' ? '−' : ''}
              {formatCurrency(step.amount, currency)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
